/* WomensHealthV3Page — Patient-centered v3 architecture (iter443).
 *
 * 6-tab top navigation: HOME · MY HEALTH JOURNEY · PROGRAMS · INSIGHTS · PROGRESS · PROFILE
 *
 * Replaces the disease-entity sidebar. Reuses existing data from PMOS, sleep_logs,
 * mood_logs, meal_log, family_history, etc. Legacy modules remain available at
 * /dashboard/womens-health/legacy (linked from Profile tab).
 *
 * Design tokens reused from "Organic & Earthy" archetype:
 *   - Fonts: Manrope (headings) + IBM Plex Sans (body)
 *   - Color tokens preserved
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import DashboardHeader from "@/components/DashboardHeader";
import SEO from "@/components/SEO";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Home, Compass, Sparkles, Lightbulb, LineChart, UserCircle2,
  Loader2, Heart, Activity, ChevronRight, CheckCircle2, Circle,
  ArrowRight, TrendingUp, TrendingDown, Minus, BookOpen, Settings,
  Moon, Brain, Salad, Info,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import "@/styles/womens-health.css";
import PmosProgramHome from "@/components/womens_health/PmosProgramHome";
import { QuickLogGrid, DriverExplainerPopover } from "@/components/womens_health/PmosV3Extras";
import WhOnboardingGate from "@/components/womens_health/WhOnboardingGate";
import WhAiBotChat from "@/components/womens_health/WhAiBotChat";
import WhGoalAndWins from "@/components/womens_health/WhGoalAndWins";
import WhFloatingAi from "@/components/womens_health/WhFloatingAi";
import { formatApiError } from "@/lib/formatApiError";

const TABS = [
  { key: "home",     icon: Home,         bg: "Начало",                en: "Home" },
  { key: "program",  icon: Sparkles,     bg: "Програма",              en: "Program" },
  { key: "insights", icon: Lightbulb,    bg: "Прозрения",             en: "Insights" },
  { key: "progress", icon: LineChart,    bg: "Напредък",              en: "Progress" },
  { key: "tools",    icon: UserCircle2,  bg: "Инструменти",           en: "Tools" },
];

const DRIVER_ICON = { stress: Brain, sleep_disruption: Moon, insulin_resistance: Activity, hyperandrogenism: Sparkles, inflammation: Activity, adiposity: Activity, low_activity: Activity, hormonal_transition: Heart, nutritional_def: Salad };

function GenderGate({ bg, isDark }) {
  return (
    <div className={`min-h-screen wh-page ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-gray-900"}`}>
      <SEO title={bg ? "Женско здраве — Nutramilo" : "Women's Health — Nutramilo"} noindex />
      <DashboardHeader />
      <main className="max-w-3xl mx-auto px-4 py-12">
        <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white"} data-testid="womens-health-v3-gated">
          <CardContent className="p-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-fuchsia-500 mx-auto flex items-center justify-center text-white mb-4">
              <Heart className="w-7 h-7" />
            </div>
            <h2 className={`text-lg font-bold mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Само за жени" : "For women only"}
            </h2>
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg
                ? "Този модул е достъпен за потребителки, посочили пол 'жена' в профила си."
                : "This module is available for users who have set gender to 'female' in their profile."}
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default function WomensHealthV3Page() {
  const { user, API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const gender = (user?.profile?.personal?.gender || "").toLowerCase();
  const isFemale = ["female", "жена", "f"].includes(gender);
  const isAdmin = user?.role === "admin";
  const allowed = isFemale || isAdmin;

  const [tab, setTab] = useState(() => {
    if (typeof window === "undefined") return "home";
    try {
      const saved = localStorage.getItem("wh-v3-tab");
      // iter501: complete tab cleanup — Home (NEW), Program, Insights, Progress, Tools.
      // Old tab keys auto-map to new ones.
      const legacyMap = {
        "ai-bot": "home",
        "journey": "home",
        "programs": "program",
        "profile": "tools",
      };
      return legacyMap[saved] || saved || "home";
    } catch { return "home"; }
  });
  useEffect(() => { try { localStorage.setItem("wh-v3-tab", tab); } catch { /* */ } }, [tab]);

  // iter451: cross-component event — any component can request to open a tool
  // (e.g., "Качи лабораторни данни" from PMOS profile card).
  const [pendingTool, setPendingTool] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      const t = e?.detail?.tool;
      if (!t) return;
      setTab("tools");
      setPendingTool(t);
    };
    window.addEventListener("wh-open-tool", handler);
    return () => window.removeEventListener("wh-open-tool", handler);
  }, []);

  if (!allowed) return <GenderGate bg={bg} isDark={isDark} />;

  return (
    <div className={`min-h-screen wh-page ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#FDFBF7] text-[#2A342B]"}`} data-testid="womens-health-v3-page">
      <SEO
        title={bg ? "Женско здраве — Nutramilo" : "Women's Health — Nutramilo"}
        description={bg ? "Програми, фактори, действия, прогрес." : "Programs, drivers, actions, progress."}
        path="/dashboard/womens-health"
      />
      <DashboardHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <header className="mb-5">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-pink-500 to-fuchsia-500 text-white shadow-lg">
              <Heart className="w-5 h-5" />
            </div>
            <h1 className="wh-heading text-2xl sm:text-3xl">
              {bg ? "Женско здраве" : "Women's Health"}
            </h1>
          </div>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-[#5C665D]"} max-w-2xl`}>
            {bg
              ? "Програми, фактори и действия — не просто резултати. Образователно, не медицинско изделие."
              : "Programs, drivers and actions — not just scores. Educational, not a medical device."}
          </p>
        </header>

        {/* Top tabs */}
        <nav
          className={`mb-5 p-1.5 rounded-2xl border flex items-center gap-1 overflow-x-auto ${
            isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-[#E5E2DA]"
          }`}
          data-testid="wh-v3-tabs"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold whitespace-nowrap transition ${
                  active
                    ? "bg-gradient-to-br from-pink-500 to-fuchsia-500 text-white shadow"
                    : isDark
                      ? "text-slate-300 hover:bg-slate-800"
                      : "text-[#5C665D] hover:bg-[#F5F3ED]"
                }`}
                data-testid={`wh-v3-tab-${t.key}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {bg ? t.bg : t.en}
              </button>
            );
          })}
        </nav>

        {/* Tab content (gated by onboarding) */}
        <WhOnboardingGate API={API} isDark={isDark}>
          <section className="space-y-5">
            {tab === "home"     && <HomeTab API={API} bg={bg} isDark={isDark} onGoProgram={() => setTab("program")} />}
            {tab === "program"  && <JourneyTab API={API} bg={bg} isDark={isDark} onGoPrograms={() => setTab("program")} />}
            {tab === "insights" && <InsightsTab API={API} bg={bg} isDark={isDark} />}
            {tab === "progress" && <ProgressTab API={API} bg={bg} isDark={isDark} />}
            {tab === "tools"    && <ProfileTab bg={bg} isDark={isDark} pendingTool={pendingTool} clearPendingTool={() => setPendingTool(null)} />}
          </section>
        </WhOnboardingGate>

        {/* iter455: floating AI assistant — replaces the standalone tab */}
        <WhFloatingAi API={API} isDark={isDark} />
      </main>
    </div>
  );
}

/* ────────────────── HOME TAB (iter501 — clean, minimal, patient-first) ──────────────────
 * Goal → Today's Action → Latest Win. That's it.
 * Quick logs appear only as contextual prompts when critical data is missing.
 */
function HomeTab({ API, bg, isDark, onGoProgram }) {
  const [data, setData] = useState(null);
  const [goal, setGoal] = useState(null);
  const [winsData, setWinsData] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [todayStatus, setTodayStatus] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, g, w, wk, ts] = await Promise.all([
        axios.get(`${API}/wh/v3/home`).then(r => r.data).catch(() => null),
        axios.get(`${API}/wh/v3/goal`).then(r => r.data).catch(() => null),
        axios.get(`${API}/wh/v3/wins?days=30`).then(r => r.data).catch(() => null),
        axios.get(`${API}/wh/v3/weekly-goal-dynamic`).then(r => r.data).catch(() => null),
        axios.get(`${API}/wh/quick-log/today-status`).then(r => r.data).catch(() => ({})),
      ]);
      setData(h); setGoal(g); setWinsData(w); setWeekly(wk); setTodayStatus(ts);
    } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("quick-log-saved", handler);
    window.addEventListener("pmos-assessment-saved", handler);
    return () => {
      window.removeEventListener("quick-log-saved", handler);
      window.removeEventListener("pmos-assessment-saved", handler);
    };
  }, [load]);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-pink-500 mx-auto my-12" />;
  if (!data) return null;

  if (!data.has_active_program) {
    return (
      <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="wh-v3-home-no-program">
        <CardContent className="p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-fuchsia-500 mx-auto flex items-center justify-center text-white mb-4">
            <Sparkles className="w-7 h-7" />
          </div>
          <h2 className="wh-heading text-xl mb-2">{bg ? "Време е да изберем програма за теб" : "Time to choose a program"}</h2>
          <Button onClick={onGoProgram} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="wh-v3-home-cta-programs">
            {bg ? "Виж програмите" : "Browse programs"}
            <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const action = data.next_actions?.[0];
  const topWin = (winsData?.wins || []).find(w => w.direction === "improving");

  // Contextual missing-data prompts (max 1)
  const MISSING_PROMPTS = [];
  if (!todayStatus.mood_logged)  MISSING_PROMPTS.push({ key: "mood",   label: "настроение", bg: "Как се чувстваш днес?" });
  if (!todayStatus.sleep_logged) MISSING_PROMPTS.push({ key: "sleep",  label: "сън",        bg: "Как спа снощи?" });
  const missingFirst = MISSING_PROMPTS[0];

  return (
    <div className="space-y-4" data-testid="wh-v3-home">
      {/* GOAL */}
      <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="wh-v3-home-goal">
        <CardContent className="p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-pink-500 to-fuchsia-500 flex items-center justify-center text-white flex-shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              Моята цел
            </p>
            <h3 className="wh-heading text-xl">
              {goal?.has_goal ? goal.label_bg : (bg ? "Все още нямаш зададена цел" : "No goal yet")}
            </h3>
            {goal?.has_goal && (
              <p className={`text-xs mt-1 ${isDark ? "text-slate-300" : "text-[#5C665D]"}`}>{goal.expected_outcome_bg}</p>
            )}
            <button
              type="button"
              onClick={onGoProgram}
              className={`text-[11px] mt-2 underline ${isDark ? "text-pink-400" : "text-pink-600"}`}
              data-testid="wh-v3-home-edit-goal"
            >
              {goal?.has_goal ? "Промени целта" : "Избери цел"}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* TODAY'S ACTION (single, large, clear) */}
      {action && (
        <Card className={isDark ? "bg-emerald-950/20 border-emerald-900" : "bg-emerald-50 border-emerald-200"} data-testid="wh-v3-home-action">
          <CardContent className="p-5">
            <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
              Днешно действие
            </p>
            <h3 className={`text-lg font-bold mb-2 ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>
              {bg ? action.bg : action.en}
            </h3>
            {weekly?.has_goal && (
              <p className={`text-[11px] ${isDark ? "text-emerald-300/80" : "text-emerald-700/80"}`}>
                <b>Цел за седмицата:</b> {weekly.weekly_target_bg}
              </p>
            )}
            <ActionCard action={action} API={API} bg={bg} isDark={isDark} onChange={load} />
          </CardContent>
        </Card>
      )}

      {/* LATEST WIN — single positive win, hero treatment */}
      {topWin && (
        <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="wh-v3-home-win">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white flex-shrink-0">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                Последна победа
              </p>
              <p className={`text-base font-bold ${isDark ? "text-slate-100" : "text-[#2A342B]"}`}>{topWin.label_bg}</p>
              <p className={`text-[11px] ${isDark ? "text-amber-300" : "text-amber-700"}`}>{topWin.delta_bg}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* MISSING DATA — single contextual prompt only */}
      {missingFirst && (
        <Card className={isDark ? "bg-amber-950/20 border-amber-900" : "bg-amber-50 border-amber-200"} data-testid={`wh-v3-home-missing-${missingFirst.key}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <Info className={`w-4 h-4 flex-shrink-0 ${isDark ? "text-amber-300" : "text-amber-700"}`} />
            <div className="flex-1 text-sm">
              <span className={isDark ? "text-amber-100" : "text-amber-900"}>{missingFirst.bg}</span>
            </div>
            <Button
              size="sm"
              onClick={() => {
                try { window.dispatchEvent(new CustomEvent("wh-open-quicklog", { detail: { mode: missingFirst.key } })); } catch { /* */ }
              }}
              className="bg-amber-500 hover:bg-amber-600 text-white text-xs"
              data-testid={`wh-v3-home-log-${missingFirst.key}`}
            >
              Запиши {missingFirst.label}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ActionCard({ action, API, bg, isDark, onChange }) {
  const [busy, setBusy] = useState(false);
  const done = action.completed_today;
  const toggle = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/wh/v3/actions/complete`, { action_id: action.id, completed: !done });
      toast.success(!done ? "Браво!" : "Отбелязано");
      onChange?.();
    } catch { toast.error("Грешка"); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={toggle} disabled={busy}
      className={`mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-bold transition ${
        done
          ? "bg-emerald-500 border-emerald-500 text-white"
          : isDark ? "bg-slate-900 border-slate-700 text-slate-100 hover:border-emerald-500" : "bg-white border-[#E5E2DA] text-[#2A342B] hover:border-emerald-400"
      }`}
      data-testid={`wh-v3-action-${action.id}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
        done ? <CheckCircle2 className="w-4 h-4" /> :
        <Circle className="w-4 h-4" />}
      {done ? "Изпълнено днес" : "Отбележи като изпълнено"}
    </button>
  );
}

/* ────────────────── JOURNEY TAB ────────────────── */
function JourneyTab({ API, bg, isDark, onGoPrograms }) {
  const [allPrograms, setAllPrograms] = useState([]);
  const [home, setHome] = useState(null);
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: progList }, { data: h }] = await Promise.all([
        axios.get(`${API}/wh/v3/programs`),
        axios.get(`${API}/wh/v3/home`),
      ]);
      setAllPrograms(progList.programs || []);
      setHome(h);
      if (h.has_active_program) {
        const { data: p } = await axios.get(`${API}/wh/v3/programs/${h.program_slug}`);
        setProgram(p);
      } else {
        setProgram(null);
      }
    } finally { setLoading(false); }
  }, [API]);
  useEffect(() => { load(); }, [load]);

  // iter451: refresh when any quick-log or assessment is saved on the page.
  useEffect(() => {
    const handler = () => { load(); };
    window.addEventListener("quick-log-saved", handler);
    window.addEventListener("pmos-assessment-saved", handler);
    return () => {
      window.removeEventListener("quick-log-saved", handler);
      window.removeEventListener("pmos-assessment-saved", handler);
    };
  }, [load]);

  const switchProgram = async (slug) => {
    if (switching || slug === home?.program_slug) return;
    setSwitching(true);
    try {
      await axios.post(`${API}/wh/v3/programs/${slug}/activate`);
      toast.success(bg ? "Превключено на нова програма" : "Switched to new program");
      await load();
    } catch (e) {
      toast.error(bg ? "Грешка при превключване" : "Switch failed");
    } finally {
      setSwitching(false);
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-pink-500 mx-auto" />;

  return (
    <div className="space-y-4" data-testid="wh-v3-journey">
      {/* iter455: Goal + Wins FIRST — patient-centric layer */}
      {home?.has_active_program && (
        <WhGoalAndWins API={API} isDark={isDark} />
      )}

      {/* iter500: Program switcher collapsed — compact pill + details accordion. */}
      <details className={`rounded-2xl border ${isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}`} data-testid="wh-v3-journey-switcher">
        <summary className="flex items-center justify-between p-3 cursor-pointer list-none">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              Активна програма
            </span>
            <span className={`text-sm font-bold truncate ${isDark ? "text-slate-100" : "text-[#2A342B]"}`}>
              {allPrograms.find(p => p.slug === home?.program_slug)?.title_bg || "—"}
            </span>
          </div>
          <span className={`text-[11px] underline ${isDark ? "text-pink-400" : "text-pink-600"}`}>
            Промени
          </span>
        </summary>
        <div className="p-3 pt-0">
          <div className="flex flex-wrap gap-2">
            {allPrograms.map((p) => {
              const isActive = p.slug === home?.program_slug;
              const disabled = !p.available || switching;
              return (
                <button
                  key={p.slug}
                  type="button"
                  disabled={disabled}
                  onClick={() => switchProgram(p.slug)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold border transition ${
                    isActive
                      ? "bg-gradient-to-br from-pink-500 to-fuchsia-500 text-white border-transparent shadow"
                      : disabled
                        ? isDark ? "bg-slate-900 border-slate-800 text-slate-600 cursor-not-allowed" : "bg-[#F5F3ED] border-[#E5E2DA] text-[#8F9990] cursor-not-allowed"
                        : isDark ? "bg-slate-900 border-slate-700 text-slate-200 hover:border-pink-500" : "bg-white border-[#E5E2DA] text-[#2A342B] hover:border-pink-400"
                  }`}
                  data-testid={`wh-v3-journey-program-${p.slug}`}
                >
                  {bg ? p.title_bg : p.title_en}
                  {!p.available && (
                    <span className="ml-1 text-[9px] opacity-60">({bg ? "скоро" : "soon"})</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </details>

      {/* iter454→455: Goal+Wins moved to TOP (above program switcher). Removed from here. */}

      {/* Quick Log — always available for logging today's data */}
      {home?.has_active_program && (
        <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="wh-v3-journey-quick-log">
          <CardContent className="p-4 sm:p-5">
            <QuickLogGrid onLogged={load} />
          </CardContent>
        </Card>
      )}

      {/* No active program → CTA only */}
      {!home?.has_active_program && (
        <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="wh-v3-journey-empty">
          <CardContent className="p-6 text-center">
            <Compass className="w-7 h-7 mx-auto mb-2 text-pink-500" />
            <p className="text-sm mb-3">
              {bg ? "Изберете програма по-горе, за да започне Вашият път." : "Pick a program above to begin your journey."}
            </p>
            <Button onClick={onGoPrograms} size="sm" className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="wh-v3-journey-cta">
              {bg ? "Виж всички програми" : "View all programs"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active program content */}
      {home?.has_active_program && home.program_slug === "pmos-reset" && (
        <PmosProgramHome bg={bg} />
      )}
      {home?.has_active_program && home.program_slug !== "pmos-reset" && program && (
        <JourneyProgramBody program={program} API={API} bg={bg} isDark={isDark} onChange={load} />
      )}
    </div>
  );
}

function JourneyProgramBody({ program, API, bg, isDark, onChange }) {
  const STAGES = [
    { key: "assess",     bg: "Оценка",        en: "Assess" },
    { key: "understand", bg: "Разбиране",     en: "Understand" },
    { key: "implement",  bg: "Прилагане",     en: "Implement" },
    { key: "optimize",   bg: "Оптимизация",   en: "Optimize" },
    { key: "maintain",   bg: "Поддръжка",     en: "Maintain" },
  ];
  const idx = STAGES.findIndex((s) => s.key === program.stage);

  return (
    <div className="space-y-4">
      {/* Stage strip */}
      <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                {bg ? "Програма" : "Program"}
              </p>
              <h2 className="wh-heading text-lg">{bg ? program.title_bg : program.title_en}</h2>
            </div>
            <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${isDark ? "bg-slate-800 text-slate-200" : "bg-[#F5F3ED] text-[#2A342B]"}`}>
              {bg ? `Ден ${program.days_in} / ${program.duration_days}` : `Day ${program.days_in} / ${program.duration_days}`}
            </span>
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {STAGES.map((s, i) => (
              <div key={s.key}
                className={`p-2 rounded-xl text-center text-[10px] sm:text-xs font-bold ${
                  i < idx ? "bg-emerald-500 text-white" :
                  i === idx ? "bg-pink-500 text-white" :
                  isDark ? "bg-slate-900 text-slate-400" : "bg-[#F5F3ED] text-[#5C665D]"
                }`}
                data-testid={`wh-v3-stage-${s.key}`}>
                {bg ? s.bg : s.en}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Driver Priority — collapsed by default (iter500 patient-centric consolidation) */}
      {program.drivers?.drivers?.length > 0 && (
        <details className={`rounded-2xl border ${isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}`} data-testid="wh-v3-journey-drivers">
          <summary className="flex items-center justify-between p-3 cursor-pointer list-none">
            <span className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              {bg ? "Какво ти пречи (виж подробности)" : "What's blocking you (details)"}
            </span>
            <ChevronRight className={`w-4 h-4 ${isDark ? "text-slate-500" : "text-[#8F9990]"}`} />
          </summary>
          <div className="p-3 pt-0">
            <ol className="space-y-2">
              {program.drivers.drivers
                .filter(d => d.value != null)
                .sort((a, b) => (b.contribution_pct ?? 0) - (a.contribution_pct ?? 0))
                .slice(0, 5)
                .map((d, i) => {
                  const Icon = DRIVER_ICON[d.key] || Activity;
                  return (
                    <li key={d.key} className="flex items-start gap-3" data-testid={`wh-v3-driver-${d.key}`}>
                      <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                        i === 0
                          ? "bg-gradient-to-br from-pink-500 to-fuchsia-500 text-white"
                          : isDark ? "bg-slate-800 text-slate-300" : "bg-[#F5F3ED] text-[#5C665D]"
                      }`}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <Icon className={`w-3.5 h-3.5 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`} />
                          <span className={`text-sm font-bold ${isDark ? "text-slate-100" : "text-[#2A342B]"}`}>
                            {bg ? d.label_bg : d.label_en}
                          </span>
                          {i === 0 && (
                            <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${isDark ? "bg-pink-950/40 text-pink-300" : "bg-pink-50 text-pink-700"}`}>
                              най-важно
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ol>
            <p className={`text-[10px] italic mt-3 ${isDark ? "text-slate-500" : "text-[#5C665D]"}`}>
              {bg
                ? `Персонализация: ${program.drivers.coverage_pct >= 60 ? "Висока" : program.drivers.coverage_pct >= 30 ? "Средна" : "Нужни са още данни"}. `
                : `Personalization: ${program.drivers.coverage_pct >= 60 ? "High" : program.drivers.coverage_pct >= 30 ? "Medium" : "More data needed"}. `}
              {bg ? program.disclaimer_bg : program.disclaimer_en}
            </p>
          </div>
        </details>
      )}

      {/* Lessons */}
      {program.lessons?.length > 0 && (
        <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="wh-v3-journey-lessons">
          <CardContent className="p-4 sm:p-5">
            <p className={`text-[10px] uppercase tracking-wider font-bold mb-3 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              {bg ? "Уроци (5-10 мин)" : "Lessons (5-10 min)"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {program.lessons.map((l) => (
                <div key={l.id}
                  className={`p-3 rounded-xl ${isDark ? "bg-slate-900/40 border border-slate-800" : "bg-[#F5F3ED] border border-[#E5E2DA]"}`}
                  data-testid={`wh-v3-lesson-${l.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-pink-300" : "text-pink-700"}`}>
                      <BookOpen className="w-3 h-3 inline mr-1" />{l.min} мин
                    </span>
                  </div>
                  <p className={`text-sm font-bold mb-1 ${isDark ? "text-white" : "text-[#2A342B]"}`}>
                    {bg ? l.title_bg : l.title_en}
                  </p>
                  <p className={`text-xs ${isDark ? "text-slate-300" : "text-[#5C665D]"}`}>
                    {bg ? l.summary_bg : l.summary_en}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's actions */}
      {program.todays_actions?.length > 0 && (
        <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="wh-v3-journey-actions">
          <CardContent className="p-4 sm:p-5">
            <p className={`text-[10px] uppercase tracking-wider font-bold mb-3 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              {bg ? "Действия за днес" : "Today's actions"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {program.todays_actions.map((a) => (
                <ActionCard key={a.id} action={a} API={API} bg={bg} isDark={isDark} onChange={onChange} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ────────────────── PROGRAMS TAB ────────────────── */
function ProgramsTab({ API, bg, isDark, onGoJourney }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/wh/v3/programs`);
      setData(d);
    } finally { setLoading(false); }
  }, [API]);
  useEffect(() => { load(); }, [load]);

  const activate = async (slug) => {
    setActivating(slug);
    try {
      await axios.post(`${API}/wh/v3/programs/${slug}/activate`);
      toast.success(bg ? "Програмата е активна" : "Program activated");
      await load();
      onGoJourney?.();
    } catch (e) {
      toast.error(formatApiError(e, (bg ? "Грешка" : "Error")));
    } finally { setActivating(null); }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-pink-500 mx-auto" />;
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="wh-v3-programs">
      <p className={`text-sm ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
        {bg
          ? "Програмите са поведенчески пътища. Активирайте една едновременно."
          : "Programs are behavior-change pathways. Activate one at a time."}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.programs.map((p) => (
          <Card key={p.slug}
            className={`${p.is_active ? "ring-2 ring-pink-500" : ""} ${isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} ${!p.available ? "opacity-70" : ""}`}
            data-testid={`wh-v3-program-${p.slug}`}>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center justify-between mb-1">
                <span className={`wh-condition-dot ${p.color_token}`} aria-hidden="true" />
                {p.is_active && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-pink-500 text-white">
                    {bg ? "Активна" : "Active"}
                  </span>
                )}
                {!p.available && (
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${isDark ? "bg-slate-800 text-slate-400" : "bg-[#F5F3ED] text-[#8F9990]"}`}>
                    {bg ? "Идва скоро" : "Coming soon"}
                  </span>
                )}
              </div>
              <h3 className="wh-heading text-lg mb-1">{bg ? p.title_bg : p.title_en}</h3>
              <p className={`text-xs mb-2 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                {bg ? p.tagline_bg : p.tagline_en}
              </p>
              <p className={`text-[10px] mb-3 ${isDark ? "text-slate-500" : "text-[#8F9990]"}`}>
                {bg ? `${p.duration_days} дни · ${p.stages.length} етапа` : `${p.duration_days} days · ${p.stages.length} stages`}
              </p>
              {p.available && !p.is_active && (
                <Button onClick={() => activate(p.slug)} disabled={activating === p.slug}
                  size="sm" className="w-full bg-pink-500 hover:bg-pink-600 text-white"
                  data-testid={`wh-v3-activate-${p.slug}`}>
                  {activating === p.slug ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5 mr-1" />}
                  {bg ? "Активирай" : "Activate"}
                </Button>
              )}
              {p.is_active && (
                <Button onClick={onGoJourney} size="sm" variant="outline" className="w-full" data-testid={`wh-v3-program-continue-${p.slug}`}>
                  {bg ? "Към моя път" : "Open my journey"}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ────────────────── INSIGHTS TAB ────────────────── */
function InsightsTab({ API, bg, isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/wh/v3/insights`);
      setData(d);
    } finally { setLoading(false); }
  }, [API]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-pink-500 mx-auto" />;

  const CONF_STYLES = {
    high:     { bg_l: "bg-emerald-50", bg_d: "bg-emerald-950/30", border: "border-emerald-500" },
    moderate: { bg_l: "bg-amber-50",   bg_d: "bg-amber-950/30",   border: "border-amber-500" },
    low:      { bg_l: "bg-slate-50",   bg_d: "bg-slate-900/40",   border: "border-slate-400" },
  };

  return (
    <div className="space-y-3" data-testid="wh-v3-insights">
      {data?.insights?.length === 0 ? (
        <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
          <CardContent className="p-6 text-center">
            <Lightbulb className="w-7 h-7 mx-auto mb-2 text-pink-500" />
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              {bg ? "Все още нямате достатъчно данни. Логвайте сън, болка, хранения." : "Not enough data yet. Log sleep, pain, meals."}
            </p>
          </CardContent>
        </Card>
      ) : (
        data?.insights?.map((ins) => {
          const c = CONF_STYLES[ins.confidence] || CONF_STYLES.moderate;
          return (
            <Card key={ins.id}
              className={`border-l-4 ${c.border} ${isDark ? c.bg_d : c.bg_l} ${isDark ? "border-slate-800" : "border-[#E5E2DA]"}`}
              data-testid={`wh-v3-insight-${ins.id}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                  <p className="wh-heading text-base">
                    {bg ? ins.insight_bg : ins.insight_en}
                  </p>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${isDark ? "bg-slate-800 text-slate-200" : "bg-white text-slate-600 border border-slate-200"}`}>
                    {bg ? `Доверие: ${ins.confidence_bg}` : `Confidence: ${ins.confidence_en}`}
                  </span>
                </div>
                <p className={`text-xs mb-2 ${isDark ? "text-slate-300" : "text-[#5C665D]"}`}>
                  <b className="uppercase tracking-wider text-[10px]">{bg ? "Доказателство" : "Evidence"}:</b> {bg ? ins.evidence_bg : ins.evidence_en}
                </p>
                <p className={`text-xs ${isDark ? "text-slate-200" : "text-[#2A342B]"}`}>
                  <b className="uppercase tracking-wider text-[10px]">{bg ? "Препоръка" : "Recommended action"}:</b> {bg ? ins.action_bg : ins.action_en}
                </p>
              </CardContent>
            </Card>
          );
        })
      )}
      <p className={`text-[10px] italic px-1 ${isDark ? "text-slate-500" : "text-[#5C665D]"}`}>
        {bg ? data?.disclaimer_bg : data?.disclaimer_en}
      </p>
    </div>
  );
}

/* ────────────────── AI BOT TAB ────────────────── */
function AiBotTab({ API, bg, isDark }) {
  const [phenotype, setPhenotype] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/wh/pcos/assessment/latest`);
        if (!cancel) setPhenotype(data?.has_assessment ? data.result : null);
      } catch { /* ignore */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [API]);

  const context = phenotype ? {
    rotterdam_phenotype: phenotype.phenotype,
    phenotype_label: phenotype.phenotype_label_bg,
    metabolic_risk: phenotype.metabolic_risk_bg,
    reproductive_risk: phenotype.reproductive_risk_bg,
  } : null;

  return (
    <div className="space-y-4" data-testid="wh-ai-bot-tab">
      <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
        <CardContent className="p-4 sm:p-5">
          <h2 className="wh-heading text-xl mb-1">{bg ? "AI Здравен Помощник" : "AI Health Assistant"}</h2>
          <p className={`text-xs ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
            {bg
              ? "Комбинирана експертиза по хранене + хормонален баланс. Цитира научни източници (PubMed). Образователно, не медицинско изделие."
              : "Combined expertise — nutrition + hormonal balance. Cites scientific sources (PubMed). Educational, not a medical device."}
          </p>
          {phenotype?.phenotype && (
            <div className={`mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] ${isDark ? "bg-violet-950/40 text-violet-200" : "bg-violet-50 text-violet-800"}`}>
              <Sparkles className="w-3 h-3" />
              <span>Контекст: {phenotype.phenotype_label_bg}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-pink-500 mx-auto" />
      ) : (
        <WhAiBotChat API={API} isDark={isDark} defaultBot="auto" context={context} />
      )}
    </div>
  );
}


/* ────────────────── PROGRESS TAB ────────────────── */
function ProgressTab({ API, bg, isDark }) {
  const [days, setDays] = useState(60);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/wh/v3/progress/timeline?days=${days}`);
      setData(d);
    } finally { setLoading(false); }
  }, [API, days]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-pink-500 mx-auto" />;

  const empty = !data?.timeline?.length;

  return (
    <div className="space-y-4" data-testid="wh-v3-progress">
      <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                {bg ? "Единен прогрес" : "Unified progress"}
              </p>
              <h2 className="wh-heading text-base">
                {bg ? "Симптоми · Сън · Настроение · Хранене · Действия" : "Symptoms · Sleep · Mood · Nutrition · Actions"}
              </h2>
            </div>
            <div className="flex gap-1.5">
              {[30, 60, 90].map((d) => (
                <Button key={d} size="sm" variant={days === d ? "default" : "outline"}
                  onClick={() => setDays(d)} className="text-xs h-7"
                  data-testid={`wh-v3-progress-range-${d}`}>
                  {d}d
                </Button>
              ))}
            </div>
          </div>

          {empty ? (
            <p className={`text-xs text-center py-6 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              {bg
                ? "Все още нямате данни за прогрес. Започнете да логвате сън, настроение или болка."
                : "No progress data yet. Start logging sleep, mood, or pain."}
            </p>
          ) : (
            <div className="overflow-x-auto" data-testid="wh-v3-progress-timeline">
              <table className="w-full text-xs">
                <thead>
                  <tr className={`border-b ${isDark ? "border-slate-800 text-slate-400" : "border-[#E5E2DA] text-[#5C665D]"}`}>
                    <th className="text-left py-2 pr-2">{bg ? "Дата" : "Date"}</th>
                    <th className="text-right py-2 px-2">{bg ? "Сън" : "Sleep"}</th>
                    <th className="text-right py-2 px-2">{bg ? "Настр." : "Mood"}</th>
                    <th className="text-right py-2 px-2">{bg ? "Стрес" : "Stress"}</th>
                    <th className="text-right py-2 px-2">{bg ? "Болка" : "Pain"}</th>
                    <th className="text-right py-2 px-2">DIL</th>
                    <th className="text-right py-2 pl-2">{bg ? "Действ." : "Actions"}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.timeline.slice().reverse().slice(0, 30).map((row) => (
                    <tr key={row.date} className={`border-b ${isDark ? "border-slate-900 text-slate-300" : "border-[#F5F3ED] text-[#2A342B]"}`}>
                      <td className="py-1.5 pr-2 font-mono text-[10px]">{row.date}</td>
                      <td className="text-right py-1.5 px-2">{row.sleep_h != null ? `${row.sleep_h}ч` : "—"}</td>
                      <td className="text-right py-1.5 px-2">{row.mood ?? "—"}</td>
                      <td className="text-right py-1.5 px-2">{row.stress ?? "—"}</td>
                      <td className="text-right py-1.5 px-2">{row.pain ?? "—"}</td>
                      <td className="text-right py-1.5 px-2">{row.dil ?? "—"}</td>
                      <td className="text-right py-1.5 pl-2">{row.actions_done ? `${row.actions_done}✓` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ────────────────── PROFILE TAB ────────────────── */
function ProfileTab({ bg, isDark, pendingTool, clearPendingTool }) {
  const [openTool, setOpenTool] = useState(null);
  useEffect(() => {
    if (pendingTool) {
      setOpenTool(pendingTool);
      clearPendingTool?.();
    }
  }, [pendingTool, clearPendingTool]);
  const tools = [
    { key: "pmos-assessment",  label_bg: "PMOS оценка",          desc_bg: "Попълнете оценка за фенотип и фактори",      type: "component", import: () => import("@/components/pmos/PmosAssessmentForm") },
    { key: "lab-upload",       label_bg: "Качване на лабораторни", desc_bg: "PDF / снимки на изследвания",               type: "component", import: () => import("@/components/womens_health/WomensHealthLabUpload") },
    { key: "fertility",        label_bg: "Фертилитет",            desc_bg: "Овулация, BBT, фертилен прозорец",          type: "component", import: () => import("@/components/fertility/FertilityModule") },
    { key: "gdpr",             label_bg: "Поверителност (GDPR)",  desc_bg: "Управление на съгласия и данни",            type: "component", import: () => import("@/components/womens_health/WomensHealthGdprPanel") },
  ];

  return (
    <div className="space-y-3" data-testid="wh-v3-profile">
      <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
        <CardContent className="p-5">
          <p className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
            Допълнителни инструменти
          </p>
          <p className={`text-sm mb-3 ${isDark ? "text-slate-300" : "text-[#5C665D]"}`}>
            Специализираните инструменти се отварят като прозорци — без напускане на тази страница.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {tools.map((t) => (
              <button key={t.key} type="button"
                onClick={() => setOpenTool(t.key)}
                className={`text-left p-3 rounded-xl border transition ${isDark ? "bg-slate-900/40 border-slate-800 hover:border-pink-500" : "bg-[#F5F3ED] border-[#E5E2DA] hover:border-pink-300"}`}
                data-testid={`wh-v3-tool-${t.key}`}>
                <p className={`text-sm font-bold ${isDark ? "text-white" : "text-[#2A342B]"}`}>{t.label_bg}</p>
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>{t.desc_bg}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {tools.map((t) => (
        <ToolDialog key={t.key} tool={t} open={openTool === t.key} onClose={() => setOpenTool(null)} isDark={isDark} />
      ))}

      <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"}>
        <CardContent className="p-5">
          <p className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
            За тази версия
          </p>
          <p className={`text-xs ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
            Версия 3 — насочена към поведение и програми. Логика: Проблем → Фактор → Действие → Напредък. Образователна, не медицинско изделие.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function ToolDialog({ tool, open, onClose, isDark }) {
  const [Comp, setComp] = useState(null);
  useEffect(() => {
    if (open && !Comp) {
      tool.import().then((mod) => setComp(() => mod.default));
    }
  }, [open, Comp, tool]);
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-700" data-testid={`wh-v3-tool-dialog-${tool.key}`}>
        <DialogHeader>
          <DialogTitle>{tool.label_bg}</DialogTitle>
          <DialogDescription>{tool.desc_bg}</DialogDescription>
        </DialogHeader>
        {Comp ? (
          <Comp bg={true} onSaved={onClose} />
        ) : (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-pink-500" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
